<template>
    <div class="source-library-table">
        <el-row class="search-bar" :gutter="10">
            <el-col :span="2">
                <el-button type="danger" size="medium" circle icon="el-icon-delete" @click="deleteSelectedSources"
                           title="Delete selected sources">
                </el-button>
                <el-button type="primary" size="medium" circle icon="el-icon-circle-plus-outline"
                           @click="openAddNewSourceDialog"
                           title="Add Source">
                </el-button>
            </el-col>
            <el-col :span="5">
                <el-input prefix-icon="el-icon-search" size="medium" v-model="sourceTable.filters.sourceName.value"
                          clearable
                          placeholder="Abbreviation / Name"></el-input>
            </el-col>
            <el-col :span="5">
                <el-select size="medium" v-model="sourceTable.filters.riskArea.value" filterable clearable
                           @change="filterQualitativeParentSource(); sourceTable.filters.sourceType.value='';sourceTable.filters.parentSource.value=''"
                           placeholder="Risk Area">
                    <el-option-group
                            v-for="group in sourceType"
                            :key="group.value"
                            :label="`${group.label} Risk Area`">
                        <el-option v-for="row in Object.values(riskAreaData).filter(obj=>obj.type === group.value)"
                                   :key="row.id"
                                   :label="row.name"
                                   :value="row.id"
                                   class="risk-area-option">
                            <span>{{ row.name }}</span>
                            <span>({{ row.abbreviation }})</span>
                        </el-option>
                    </el-option-group>
                </el-select>
            </el-col>
            <el-col :span="3">
                <el-select size="medium" v-model="sourceTable.filters.sourceType.value" clearable
                           :disabled="Boolean(sourceTable.filters.riskArea.value)"
                           @change="sourceTable.filters.parentSource.value=''"
                           placeholder="Source Type">
                    <el-option v-for="obj in sourceType" :key="obj.value" :label="obj.label"
                               :value="obj.value"></el-option>
                </el-select>
            </el-col>
            <el-col :span="3">
                <el-select
                        :disabled="disableParentSourceFilter"
                        filterable default-first-option
                        :filter-method="filterQualitativeParentSource"
                        v-model="sourceTable.filters.parentSource.value"
                        clearable
                        size="medium"
                        placeholder="Parent Source">
                    <el-option
                            v-for="obj in sourceTable.qualitativeParentSources"
                            :label="obj.abbreviation"
                            class="parent-source-filter"
                            :value="obj.id"
                            :key="obj.abbreviation">
                        <span>{{ obj.name }}</span>
                        <span>({{ obj.abbreviation }})</span>
                    </el-option>
                </el-select>
            </el-col>
            <el-col :span="3">
                <el-select size="medium" v-model="sourceTable.filters.sourceStatus.value" clearable
                           placeholder="Source Status">
                    <el-option v-for="obj in sourceStatus" :key="obj.value" :label="obj.label"
                               :value="obj.value"></el-option>
                </el-select>
            </el-col>
            <el-col :span="3">
                <el-select :disabled="!sourceTable.filters.riskArea.value"
                           clearable
                           size="medium"
                           v-model="sourceTable.filters.includedExcluded.value"
                           placeholder="Excluded / Included">
                    <el-option label="Included" :value="true"></el-option>
                    <el-option label="Excluded" :value="false"></el-option>
                </el-select>
            </el-col>
        </el-row>
        <data-tables
                ref="sourceTable"
                :data="sourceTable.data"
                v-loading="sourceTable.loading"
                :pageSize="10"
                :pagination-props="sourceTable.paginationProps"
                border
                style="width: 100%"
                :filters="Object.values(sourceTable.filters)">
            <el-table-column type="selection" width="50" align="center"></el-table-column>
            <el-table-column prop="abbreviation" label="Abbreviation" :sortable="true" width="150"></el-table-column>
            <el-table-column prop="name" label="Name" :sortable="true">
                <template slot-scope="scope">
                    <span class="grey-name"> {{scope.row.name}}</span>
                    <el-popover v-if="getRiskAreaNames(scope.row).length"
                                placement="top-start"
                                title="Risk Areas"
                                width="300"
                                trigger="hover" style="display: inline-flex;">
                        <div v-if="Boolean(Object.keys(riskAreaData).length)" class="risk-area-popover-content">
                            <div v-if="getRiskAreaNames(scope.row).length" class="included">
                                <h3>Included</h3>
                                <p v-for="name in getRiskAreaNames(scope.row)" :key="name" v-text="name"></p>
                            </div>
                            <!--<div v-if="getRiskAreaNames(scope.row, false).length" class="excluded">
                                <h3>Excluded</h3>
                                <p v-for="name in getRiskAreaNames(scope.row, false)" :key="name" v-text="name"></p>
                            </div>-->
                        </div>
                        <div slot="reference">
                            <span class="count-badge">{{getRiskAreaNames(scope.row).length}}</span>
                        </div>
                    </el-popover>
                </template>
            </el-table-column>
            <el-table-column width="40">
                <template slot-scope="scope">
                    <el-link icon="el-icon-link" :underline="false"
                             :href="scope.row.url" target="_blank" v-if="scope.row.url"
                             :title="scope.row.url"></el-link>
                </template>
            </el-table-column>
            <el-table-column
                    v-if="sourceTable.filters.sourceType.value === 'qual' || Object.values(riskAreaData).filter(obj=>(obj.type === 'qual' && obj.id === sourceTable.filters.riskArea.value)).length"
                    prop="parent_source_id" label="Is Parent" :sortable="true" width="110">
                <template slot-scope="props">
                    <span v-text="props.row.parent_source_id?'No':'Yes'"></span>
                </template>
            </el-table-column>
            <el-table-column prop="type" label="Type" :sortable="true" width="100">
                <template slot-scope="props">
                    <span v-text="sourceType.find(obj=>obj.value===props.row.type).label"></span>
                </template>
            </el-table-column>
            <el-table-column prop="status" label="Status" width="120">
                <template slot-scope="props">
                    <span class="capitalize"
                          v-text="props.row.status=='analysis'?'Under Analysis':props.row.status"></span>
                </template>
            </el-table-column>
            <el-table-column
                    prop="date_expected_publish" :formatter="(row, column, cellValue)=>formatDateYMD(cellValue)"
                    label="Expected Publish Date" :sortable="true" width="200">
            </el-table-column>
            <el-table-column width="120">
                <template slot-scope="scope">
                    <el-tooltip effect="dark" :open-delay="1000" content="Upload History" placement="top">
                        <el-button :disabled="!scope.row['enable_history']"
                                   @click.native.prevent="openUploadHistory(scope.row)"
                                   icon="el-icon-notebook-1"
                                   type="text">
                        </el-button>
                    </el-tooltip>
                    <el-tooltip effect="dark" :open-delay="1000"
                                content="Add to Risk Area"
                                placement="top">
                        <el-button
                                :disabled="scope.row.parent_source_id && scope.row.type==='qual'"
                                @click.native.prevent="openSourceDetailsDialogBySource(scope.row)"
                                icon="el-icon-circle-plus-outline"
                                type="text">
                        </el-button>
                    </el-tooltip>
                    <el-tooltip effect="dark" :open-delay="1000" content="Edit Source" placement="top">
                        <el-button
                                @click.native.prevent="openAddNewSourceDialog(scope.row)"
                                icon="el-icon-edit-outline"
                                type="text">
                        </el-button>
                    </el-tooltip>
                </template>
            </el-table-column>
        </data-tables>
        <el-dialog title="View Upload History"
                   :visible.sync="uploadHistoryDialog.visible"
                   width="60%"
                   class="upload-history-dialog"
                   :close-on-click-modal="false"
                   @close="uploadHistoryDialog.compare.filter_name=''"
                   @closed="uploadHistoryDialog.fullscreen=true"
                   :fullscreen="uploadHistoryDialog.fullscreen">
            <div slot="title" class="dialog-title">
                <b>Upload History</b>
                <el-divider direction="vertical"></el-divider>
                <span v-text="sourceTable.selectedRow.name"></span>
                <el-divider direction="vertical"></el-divider>
                <span v-text="sourceTable.selectedRow.abbreviation"></span>
                <!--                <el-button @click="uploadHistoryDialog.fullscreen = !uploadHistoryDialog.fullscreen" class="full-screen" icon="el-icon-full-screen"></el-button>-->
            </div>

            <div class="dialog-content">
                <div class="top-btn">
                    <el-dropdown size="mini" class="actionBtn"
                                 v-if="$refs.sourceHistoryTable"
                                 @command="handleAction">
                        <el-button type="primary" size="mini" :loading="uploadHistoryDialog.historyTable.loadingBtn">
                            <span v-text="'Action ('+$refs.sourceHistoryTable.$refs.elTable.selection.length+')'"></span><i
                                class="el-icon-arrow-down el-icon--right pull-right"></i>
                        </el-button>
                        <el-dropdown-menu slot="dropdown">
                            <el-dropdown-item v-for="(value, key) in statusMapping" :key="key" :command="key"
                                              :class="$refs.sourceHistoryTable.$refs.elTable.selection.length == 0 || key==3 ? 'disabled' : ''">
                                {{value}}
                            </el-dropdown-item>
                            <el-dropdown-item command="delete" divided
                                              :class="$refs.sourceHistoryTable.$refs.elTable.selection.length == 0 ? 'disabled' : ''">
                                Delete
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </div>
                <data-tables
                        :data="uploadHistoryDialog.historyTable.data"
                        :pageSize="10"
                        ref="sourceHistoryTable"
                        class="source-history-table"
                        :filters="uploadHistoryDialog.historyTable.filters"
                        :table-props="uploadHistoryDialog.historyTable.tableProps"
                        layout="pagination, table"
                        border
                        :pagination-props="uploadHistoryDialog.historyTable.paginationProps"
                        style="width: 100%"
                        v-loading="uploadHistoryDialog.historyTable.loading"
                        @selection-change="handleSelectionChange"
                >
                    <el-table-column type="selection" align="center"></el-table-column>
                    <el-table-column prop="id" label="#ID" width="80"></el-table-column>
                    <el-table-column prop="source_publication_date" label="Data Date" width="150">
                        <template slot-scope="props">
                            <el-date-picker type="date" ref="input_source_publication_date" placeholder="Pick a date"
                                            size="mini"
                                            format="yyyy-MM-dd"
                                            v-loading="props.row.loading_source_publication_date"
                                            element-loading-spinner="el-icon-loading"
                                            value-format="yyyy-MM-dd"
                                            :clearable="false"
                                            @change="editIndexDataSet(props.row, 'source_publication_date')"
                                            v-model="props.row['source_publication_date']"></el-date-picker>
                        </template>
                    </el-table-column>
                    <el-table-column prop="source_publication_display_date" label="Display Year" width="110">
                        <template slot-scope="props">
                            <div v-loading="props.row.loading_source_publication_display_date"
                                 element-loading-spinner="el-icon-loading">
                                <el-input size="mini" :ref="'input_source_publication_display_date_'+props.row.id"
                                          placeholder="Enter Year"
                                          v-model="props.row['source_publication_display_date']"
                                          @change="editIndexDataSet(props.row, 'source_publication_display_date')"
                                ></el-input>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column prop="date_last_confirmed" label="Date Last Confirmed" width="225">
                        <template slot-scope="props">
                            <span v-text="formatDateYMD(props.row['date_last_confirmed'])"></span>
                            <el-button :loading="props.row.loadingDateLastConfirmed"
                                       class="last-confirmed-btn"
                                       size="mini"
                                       type="primary"
                                       @click="saveDateLastConfirmed(props.row)"
                                       icon="el-icon-circle-check">
                                Confirmed today
                            </el-button>
                        </template>
                    </el-table-column>
                    <el-table-column v-if="sourceTable.selectedRow.type=='quant'" prop="normalization_lower_bound"
                                     label="Lower / Upper Bound">
                        <template slot-scope="props">
                            <div v-loading="props.row.loading_normalization_bounds"
                                 class="change-normalization-div"
                                 element-loading-spinner="el-icon-loading">
                                <el-row :gutter="10">
                                    <el-col :span="12">
                                        <el-input size="mini" :ref="'input_normalization_lower_bound_'+props.row.id"
                                                  placeholder="Enter Lower Bound"
                                                  v-model="props.row['normalization_lower_bound']"
                                        ></el-input>
                                    </el-col>
                                    <el-col :span="12">
                                        <el-input size="mini" :ref="'input_normalization_upper_bound_'+props.row.id"
                                                  placeholder="Enter Upper Bound"
                                                  v-model="props.row['normalization_upper_bound']"
                                        ></el-input>
                                    </el-col>
                                </el-row>
                                <el-button :disabled="props.row.loading_normalization_bounds" size="mini" type="primary"
                                           @click="saveNormalizationValues(props.row)">
                                    Save
                                </el-button>
                            </div>
                        </template>
                    </el-table-column>
                    <el-table-column label="Country Count">
                        <template slot-scope="props">{{Object.keys(sourceTable.selectedRow.type=='quant' ?
                            props.row.index_score : props.row.qualitative_score).length}}
                        </template>
                    </el-table-column>
                    <el-table-column prop="status" width="75" label="Status" align="center">
                        <template slot-scope="props">
                            <span v-text="statusMapping[props.row.status]"></span>
                        </template>
                    </el-table-column>
                </data-tables>
            </div>
            <el-collapse-transition>
                <el-card class="box-card"
                         :class="sourceTable.selectedRow.type == 'quant' ? 'quantitative-table' : 'qualitative-table'"
                         v-show="uploadHistoryDialog.compare.data.length && !uploadHistoryDialog.historyTable.loading">
                    <div slot="header">
                        <el-tooltip effect="dark" :open-delay="1000" content="Add New Row" placement="top">
                            <el-button :disabled="uploadHistoryDialog.compare.enableAdd" type="primary"
                                       @click.native.prevent="addNewRow()" size="mini">
                                <i class="el-icon-circle-plus-outline el-icon--right pull-right"></i>
                                Add Row
                            </el-button>
                        </el-tooltip>
                        <el-button v-if="$refs.sourceHistoryTable" type="text" icon="el-icon-close"
                                   @click="closeComparisionDialog()"></el-button>
                    </div>
                    <el-table
                            stripe
                            :data="uploadHistoryDialogCompareData">
                        <el-table-column prop="country_name" key="country_name" width="270">
                            <template slot="header" slot-scope="{}">
                                <el-input placeholder="Search Country..." prefix-icon="el-icon-search" clearable
                                          size="mini" v-model="uploadHistoryDialog.compare.filter_name"></el-input>
                            </template>
                            <template slot-scope="props">
                                <el-autocomplete :disabled="disableCountry(props.row)"
                                                 :highlight-first-item="true"
                                                 class="inline-input"
                                                 v-model="props.row.country_name"
                                                 size="mini"
                                                 :fetch-suggestions="countrySearch"
                                                 placeholder="Select Country"
                                                 @select="handleCountrySelect($event, props.row)"
                                ></el-autocomplete>
                            </template>
                        </el-table-column>
                        <el-table-column v-for="row in uploadHistoryDialog.compare.titles" :label="row.label"
                                         :key="row.key" align="center">
                            <el-table-column label="Raw Score" align="center" min-width="100"
                                             v-if="sourceTable.selectedRow.type=='quant'">
                                <template slot-scope="props">
                                    <el-input :disabled="props.row.country_id==''"
                                              class="raw-score"
                                              @change="recalculateNormalizedValue(props.row[row['key']],
                                                 props.row.country_id)"
                                              v-model="props.row[row['key']]['raw_score']"
                                              size="mini"></el-input>
                                </template>
                            </el-table-column>
                            <el-table-column label="Qualitative Value" align="center" min-width="100"
                                             v-if="sourceTable.selectedRow.type=='qual'">
                                <template slot-scope="props">
                                    <el-select :disabled="props.row.country_id==''" size="medium"
                                               v-model="props.row[row['key']]['value_id']" filterable
                                               placeholder="Select"
                                               @change="changeQualitativeScore(props.row[row['key']], props.row.country_id)">
                                        <el-option v-for="row in qualityValueData" :key="row.id" :label="row.label"
                                                   :value="row.id">
                                            <span style="text-transform: capitalize;">{{ row.label }}</span>
                                        </el-option>
                                    </el-select>
                                </template>
                            </el-table-column>
                            <el-table-column label="Normalized Score" align="center" min-width="140"
                                             v-if="sourceTable.selectedRow.type=='quant'">
                                <template slot-scope="props" v-if="props.row[row['key']]">
                                    <span v-if="props.row[row['key']]['loading_raw_score']"><i
                                            class="el-icon-loading"></i></span>
                                    <span v-else v-text="props.row[row['key']]['normalized_score']"></span>
                                </template>
                            </el-table-column>
                            <el-table-column label="" align="center" min-width="50">
                                <template slot-scope="props"
                                          v-if="props.row[row['key']]['normalized_score'] || props.row[row['key']]['value_id']">
                                    <el-tooltip effect="dark"
                                                :open-delay="1000"
                                                content="Delete Score"
                                                placement="top">
                                        <el-button class="btn-delete"
                                                   type="text"
                                                   @click.native.prevent="deleteIndexScore(props.row[row['key']])"
                                                   icon="el-icon-remove-outline">
                                        </el-button>
                                    </el-tooltip>
                                </template>
                            </el-table-column>
                        </el-table-column>
                    </el-table>
                </el-card>
            </el-collapse-transition>
        </el-dialog>
        <add-new-source-dialog ref="addNewSourceDialog"
                               v-bind:loading.sync="sourceTable.loading">
        </add-new-source-dialog>
        <source-detail-dialog-by-source ref="sourceDetailsDialogBySource"
                                        v-bind:loading.sync="sourceTable.loading"></source-detail-dialog-by-source>
    </div>
</template>

<script>
    import {API} from "@/api/index";
    import {createNamespacedHelpers} from 'vuex';
    import AddNewSourceDialog from './../components/AddNewSourceDialog.vue';
    import SourceDetailDialogBySource from './../components/SourceDetailDialogBySource.vue';

    const {mapState, mapActions} = createNamespacedHelpers('riskIndex');

    export default {
        name:        "ManageSources",
        components:  {AddNewSourceDialog, SourceDetailDialogBySource},
        data() {
            return {
                sourceType:          [
                    {
                        value: 'quant',
                        label: 'Quantitative'
                    }, {
                        value: 'qual',
                        label: 'Qualitative'
                    }
                ],
                sourceStatus:        [
                    {
                        value: 'active',
                        label: 'Active'
                    }, {
                        value: 'analysis',
                        label: 'Under Analysis'
                    }, {
                        value: 'inactive',
                        label: 'Inactive'
                    }
                ],
                qualityType:         [],
                sourceTable:         {
                    data:                     [],
                    loading:                  true,
                    selectedRow:              {},
                    paginationProps:          {
                        background: true,
                        pageSizes:  [10, 20, 30]
                    },
                    filters:                  {
                        riskArea:         {
                            prop:     'risk_area',
                            value:    '',
                            filterFn: (row, filter) => {
                                return row.risk_areas.includes(filter.value);
                            }
                        },
                        sourceType:       {
                            prop:     'type',
                            value:    '',
                            filterFn: (row, filters) => {
                                return row.type === filters.value;
                            }
                        },
                        parentSource:     {
                            prop:     'parent_source_id',
                            value:    '',
                            filterFn: (row, filters) => {
                                return row.parent_source_id === filters.value;
                            }
                        },
                        sourceStatus:     {
                            prop:     'is_active',
                            value:    '',
                            filterFn: (row, filters) => {
                                return row.status === filters.value;
                            }
                        },
                        sourceName:       {
                            prop:     'name',
                            value:    '',
                            filterFn: (row, filters) => {
                                return row.name.toLowerCase().indexOf(filters.value.toLowerCase()) >= 0 || row.abbreviation.toLowerCase().indexOf(filters.value.toLowerCase()) >= 0;
                            }
                        },
                        includedExcluded: {
                            prop:     '',
                            value:    '',
                            filterFn: (row, filters) => {
                                let riskAreaFilterValue = this.sourceTable.filters.riskArea.value;
                                if (!riskAreaFilterValue) {
                                    this.sourceTable.filters.includedExcluded.value = '';
                                    return true;
                                }
                                let sourceWeight = this.sourceWeightData[`${riskAreaFilterValue}-${row.id}`];
                                return Boolean(sourceWeight) && (filters.value ? sourceWeight.weight > 0 : sourceWeight.weight == 0);
                            }
                        }
                    },
                    qualitativeParentSources: []
                },
                uploadHistoryDialog: {
                    visible:      false,
                    fullscreen:   true,
                    historyTable: {
                        loading:         true,
                        data:            [],
                        filters:         [],
                        paginationProps: {
                            background: true,
                            pageSizes:  [10, 20, 30],
                            small:      true,
                            layout:     "prev, pager, next"
                        },
                        tableProps:      {
                            'height': 'calc(30vh)'
                        },
                        loadingBtn:      false
                    },
                    compare:      {
                        data:        [],
                        titles:      [],
                        filter_name: '',
                        enableAdd:   false
                    }
                },
                statusMapping:       {
                    0: 'Inactive',
                    1: 'Current',
                    2: 'Historic',
                    3: 'Draft'
                }
            };
        }, computed: {
            uploadHistoryDialogCompareData: function () {
                let self        = this;
                let filter_name = self.uploadHistoryDialog.compare.filter_name.toLowerCase();
                let data        = self.uploadHistoryDialog.compare.data;
                if (data.length == 0)
                    self.uploadHistoryDialog.compare.enableAdd = false;
                let hasRawScoreInFirstRow = false;
                if (data.length > 0) {
                    for (const [key, value] of Object.entries(data[0])) {
                        if (key != 'country_name' && key != 'country_id' && value.id != undefined)
                            hasRawScoreInFirstRow = true;
                    }
                }
                if (self.uploadHistoryDialog.compare.enableAdd && hasRawScoreInFirstRow) {
                    let dummy = JSON.parse(JSON.stringify(data[0]));
                    for (const [key] of Object.entries(dummy)) {
                        if (key == 'country_name' || key == 'country_id')
                            dummy[key] = '';
                        else {
                            dummy[key]['country_id']       = '';
                            dummy[key]['id']               = undefined;
                            dummy[key]['normalized_score'] = undefined;
                            dummy[key]['raw_score']        = undefined;
                            dummy[key]['value_id']         = undefined;
                        }
                    }
                    self.uploadHistoryDialog.compare.data.unshift(dummy);
                }
                if (!filter_name)
                    return data;
                return data.filter(row => row.country_name.toLowerCase().includes(filter_name));
            },
            countryList:                    function () {
                let countryList       = Object.values(this.countryData).map(row => ({
                    country_id: row.id,
                    value:      row.name
                }));
                let enlistedCountries = this.uploadHistoryDialog.compare.data.map(country => country.country_id);
                return countryList.filter(({country_id}) => !enlistedCountries.includes(country_id));
            },
            disableParentSourceFilter() {
                let filterHasQualRiskArea = this.sourceTable.filters.riskArea.value && Object.values(this.riskAreaData).filter(row => row.id === this.sourceTable.filters.riskArea.value && row.type === 'qual').length;
                return !(filterHasQualRiskArea || this.sourceTable.filters.sourceType.value === 'qual');
            },
            ...mapState(['countryData', 'sourceData', 'riskAreaData', 'sourceWeightData', 'qualityValueData'])
        },
        methods:     {
            ...mapActions([
                'loadCountryData', 'loadSourceData', 'loadSourceWeightData', 'loadQualitativeValueData'
            ]),
            formatDateYMD:                      function (timestamp) {
                return timestamp ? new Date(timestamp * 1000).toISOString().substr(0, 10) : 'NA';
            },
            openAddNewSourceDialog:             function (editRow = false) {
                this.sourceTable.selectedRow = Object.assign({referenceObj: editRow}, editRow);
                this.$refs.addNewSourceDialog.showAddNewSourceDialog(editRow);
            },
            constructSourceTableData:           function () {
                let self                 = this;
                self.sourceTable.loading = true;
                if (!Object.keys(this.sourceData).length)
                    return;
                self.sourceTable.data = Object.values(this.sourceData);
                self.$nextTick(function () {
                    self.$refs.sourceTable.$refs.elTable.sort('abbreviation', 'ascending');
                });
                self.sourceTable.loading = false;
                this.setQualitativeParentSources();
            },
            deleteSelectedSources:              async function () {
                let selectedSources = this.$refs.sourceTable.$refs.elTable.selection;
                if (!selectedSources.length)
                    return;
                let confirm = await this.$confirm(`Are you sure you want to delete selected sources?`, 'Warning', {
                    confirmButtonText: 'OK', cancelButtonText: 'Cancel', type: 'warning'
                }).then(() => true).catch(() => false);
                if (!confirm) return;
                this.sourceTable.loading = true;
                await this.$refs.addNewSourceDialog.deleteSource(selectedSources);
                this.sourceTable.loading = false;
            },
            getRiskAreaNames:                   function (row, included = true) {
                return Object.values(this.riskAreaData).filter(obj => {
                    if (!(row.risk_areas && row.risk_areas.includes(obj.id)))
                        return false;

                    let sourceWeight = this.sourceWeightData[`${obj.id}-${row.id}`];
                    return Boolean(sourceWeight) && (included ? sourceWeight.weight > 0 : sourceWeight.weight == 0);
                }).map(x => x.name);
            }, openSourceDetailsDialogBySource: function (row) {
                this.$refs.sourceDetailsDialogBySource.showSourceDetails(row);
            },
            openUploadHistory:                  async function (row) {
                let self                         = this;
                self.uploadHistoryDialog.visible = true;
                self.sourceTable.selectedRow     = Object.assign({referenceObj: row}, row);

                let table     = self.uploadHistoryDialog.historyTable;
                table.loading = true;
                table.data    = [];
                await self.loadCountryData(false);
                if (self.sourceTable.selectedRow.type == 'qual')
                    await self.loadQualitativeValueData(false);
                let resp = await API.riskIndex.sourceHistory.get({id: row.id});

                let indexOfCurrentDataSet = 0;
                resp.forEach((row, index) => {
                    row.loading_source_publication_display_date = false;
                    row.loading_normalization_bounds            = false;
                    row.loading_source_publication_date         = false;
                    row.loadingDateLastConfirmed                = false;
                    if (row.status === 1) {
                        indexOfCurrentDataSet = index;
                    }
                });
                if (indexOfCurrentDataSet >= 0)
                    resp.unshift(resp.splice(indexOfCurrentDataSet, 1)[0]);

                table.data    = resp;
                table.loading = false;
            },
            handleSelectionChange(selectedRowData) {
                let compareTableData = {};
                let data             = selectedRowData;
                data.forEach(batch => {
                    batch['index_score'].forEach(index_score_row => {
                        compareTableData[index_score_row['country_id']] = compareTableData[index_score_row['country_id']] || {};
                        Object.assign(compareTableData[index_score_row['country_id']], {
                            country_name:                                   index_score_row['country_id'] ? this.countryData[index_score_row['country_id']].name : '',
                            [String(index_score_row['index_data_set_id'])]: Object.assign(index_score_row, {loading_raw_score: false}),
                            country_id:                                     index_score_row['country_id']
                        });
                    });
                });
                data.forEach(batch => {
                    batch['qualitative_score'].forEach(qualitative_score_row => {
                        compareTableData[qualitative_score_row['country_id']] = compareTableData[qualitative_score_row['country_id']] || {};
                        Object.assign(compareTableData[qualitative_score_row['country_id']], {
                            country_name:                                         qualitative_score_row['country_id'] ? this.countryData[qualitative_score_row['country_id']].name : '',
                            [String(qualitative_score_row['index_data_set_id'])]: qualitative_score_row,
                            country_id:                                           qualitative_score_row['country_id']
                        });
                    });
                });
                let titles           = data.map(row => ({
                    key:   String(row.id),
                    label: `${row['source_publication_date']} (#${row.id})`
                }));
                compareTableData     = Object.values(compareTableData);
                let selectedBacthIds = selectedRowData.map(row => row.id);
                compareTableData.forEach(row => {
                    selectedBacthIds.forEach(batchId => {
                        if (!row[batchId] && this.sourceTable.selectedRow.type == 'quant') {
                            row[batchId] = {
                                country_id:        row['country_id'],
                                id:                undefined,
                                index_data_set_id: batchId,
                                normalized_score:  undefined,
                                raw_score:         undefined
                            };
                        }
                        if (!row[batchId] && this.sourceTable.selectedRow.type == 'qual') {
                            row[batchId] = {
                                country_id:        row['country_id'],
                                id:                undefined,
                                index_data_set_id: batchId,
                                value_id:          undefined
                            };
                        }
                    });
                });
                this.uploadHistoryDialog.compare.data   = compareTableData;
                this.uploadHistoryDialog.compare.titles = titles;
            },
            handleAction(command) {
                if (this.$refs.sourceHistoryTable.$refs.elTable.selection.length == 0) {
                    this.$notify.error({
                        title:   'Row not selected',
                        message: `You have not selected any row.`
                    });
                    return;
                }
                if (command == 'delete') {
                    this.deleteRow();
                    return;
                }
                this.updateStatus(Number(command));

            },
            async deleteRow() {
                let selectedRows  = this.$refs.sourceHistoryTable.$refs.elTable.selection;
                let checkActive   = selectedRows.findIndex(row => row['status'] === 1);
                let checkHistoric = selectedRows.findIndex(row => row['status'] === 2);
                if (checkActive >= 0 || checkHistoric >= 0) {
                    this.$notify.error({
                        title:   'Row cannot be deleted.',
                        message: `If the status of any selected row is ${checkActive >= 0 ? `current` : `historic`}, you cannot delete it.`
                    });
                    return;
                }

                let confirm = await this.$confirm(`Are you sure you want to delete selected sources?`, 'Warning', {
                    confirmButtonText: 'OK', cancelButtonText: 'Cancel', type: 'warning'
                }).then(() => true).catch(() => false);
                if (!confirm) return;

                let table     = this.uploadHistoryDialog.historyTable;
                let tableData = this.$refs.sourceHistoryTable.$refs.elTable.selection;
                table.loading = true;
                let response  = await API.riskIndex.indexDataSet.delete(tableData.map(row => ({id: row['id']})));
                if (response.body.data[0].status == 'deleted') {
                    table.data = table.data.filter(row => !tableData.map(rec => rec['id']).includes(row['id']));
                } else {
                    this.$notify.error({
                        title:   `Delete unsuccessful`,
                        message: `Error while deleting the selected row #${selectedRows[0].id}.`
                    });
                }
                table.loading = false;
            },
            async updateStatus(status) {
                let selectedRows = this.$refs.sourceHistoryTable.$refs.elTable.selection;
                let checkActive  = selectedRows.findIndex(row => row['status'] === 1);
                //let checkHistoric = selectedRows.findIndex(row => row['status']===2);
                if (checkActive >= 0) {
                    this.$notify.error({
                        title:   `Cannot change status ${this.statusMapping[status]}`,
                        message: `If the status of any selected row is current, you cannot change it to ${this.statusMapping[status]}.`
                    });
                    return;
                }
                if (status == 1 && selectedRows.length > 1) {
                    this.$notify.error({
                        title:   'More than one row selected.',
                        message: `You have selected more than one row so, status cannot be changed to current.`
                    });
                    return;
                }
                if (status == 3) {
                    this.$notify.error({
                        title:   'You cannot change the status to draft.',
                        message: `When you upload a new data, status draft will be assigned to it.`
                    });
                    return;
                }
                let confirm = await this.$confirm(`Are you sure you want to update source to ${this.statusMapping[status]}?`, 'Warning', {
                    confirmButtonText: 'OK', cancelButtonText: 'Cancel', type: 'warning'
                }).then(() => true).catch(() => false);
                if (!confirm) return;
                let table        = this.uploadHistoryDialog.historyTable;
                table.loading    = true;
                table.loadingBtn = true;

                let payload = selectedRows.map(list => Object.assign({
                    'id':     list['id'],
                    'status': status
                }));
                await API.riskIndex.indexDataSet.post(payload);
                await this.openUploadHistory(this.sourceTable.selectedRow);
                table.loading    = false;
                table.loadingBtn = false;
            },
            async saveNormalizationValues(row) {
                row.loading_normalization_bounds = true;
                let payload                      = {
                    id:                          row.id,
                    'normalization_lower_bound': row.normalization_lower_bound,
                    'normalization_upper_bound': row.normalization_upper_bound
                };

                let resp = await API.riskIndex.indexDataSet.put(payload);

                row.loading_normalization_bounds = false;

                if (!resp) return;

                if (resp && resp.body.data.length) {
                    let savedObject = resp.body.data[0];
                    let localObject = this.uploadHistoryDialog.historyTable.data.filter(batch => batch.id === row.id)[0];
                    if (savedObject.id === localObject.id) {
                        localObject['index_score'] = [];
                        // update parent local data
                        localObject['index_score'].push(...savedObject['index_score']);

                        let savedIdBasedObject = savedObject['index_score'].reduce((acc, cur) => {
                            acc[cur.id] = cur;
                            return acc;
                        }, {});
                        // update compare table data
                        this.uploadHistoryDialogCompareData.map(data => data[row.id]).forEach(temp => {
                            if (savedIdBasedObject[temp.id])  // if deleted the object with temp.id does not exist
                                temp.normalized_score = savedIdBasedObject[temp.id]['normalized_score'];
                        });
                    }
                }

                this.$notify.success({
                    title:   `#${row.id} updated successfully`,
                    message: `Successfully updated lower & upper bounds for #${row.id}`
                });
            },
            async editIndexDataSet(row, element) {
                row['loading_' + element] = true;
                var payload               = {[element]: row[element]};
                let resp                  = await API.riskIndex.indexDataSet.put(
                    Object.assign(payload, {"id": row.id}));
                row['loading_' + element] = false;
                if (!resp) {
                    this.$refs['input_' + element + '_' + row.id].focus();
                    return;
                }
                this.$notify.success({
                    title:   `#${row.id} updated successfully`,
                    message: `Successfully updated ${element} for #${row.id}`
                });
            },
            async saveDateLastConfirmed(row) {
                row.loadingDateLastConfirmed = true;
                let dateLastConfirmed        = Math.floor(+new Date(+new Date() - (new Date().getTimezoneOffset() * 60000)) / 1000);

                let resp = await API.riskIndex.importBatch.put({
                    "id":                  row['import_id'],
                    "date_last_confirmed": dateLastConfirmed
                });
                if (!resp) return;
                row.loadingDateLastConfirmed = false;
                row['date_last_confirmed']   = resp.data.data[0].date_last_confirmed;
                this.$notify.success({
                    title:   `#${row.id} updated successfully`,
                    message: `Successfully updated today as Date Last Confirmed for #${row.id}`
                });
            },
            recalculateNormalizedValue:         async function (row, country_id) {
                if (row.raw_score == '') {
                    this.$notify.error({
                        title:   'Empty Raw score',
                        message: `Raw score cannot be empty.`
                    });
                    return;
                }
                row.loading_raw_score = true;
                let response;
                if (row.id) {
                    response = await API.riskIndex.indexScore.put({
                        id:        row.id,
                        raw_score: row.raw_score
                    });
                } else {
                    response = await API.riskIndex.indexScore.post([
                        {
                            index_data_set_id: row.index_data_set_id,
                            country_id:        country_id,
                            raw_score:         row.raw_score
                        }
                    ]);
                }
                if (response && response.body.data.length) {
                    let savedObject = response.data.data[0];
                    if (!row.id) {
                        let batchObject = this.uploadHistoryDialog.historyTable.data.filter(batch => batch.id === row.index_data_set_id)[0];
                        batchObject['index_score'].unshift(savedObject);
                        this.uploadHistoryDialog.compare.enableAdd = false;
                    }
                    Object.assign(row, savedObject);
                }
                row.loading_raw_score = false;
            },
            changeQualitativeScore:             async function (row, country_id) {
                if (row['value_id'] == '') {
                    this.$notify.error({
                        title:   'Empty Qualitative Value',
                        message: `Qualitative Value cannot be empty.`
                    });
                    return;
                }
                let response;
                if (row.id) {
                    response = await API.riskIndex.qualitativeScore.put({
                        id:       row['id'],
                        value_id: row['value_id']
                    });
                } else {
                    response = await API.riskIndex.qualitativeScore.post([
                        {
                            country_id:        country_id,
                            index_data_set_id: row['index_data_set_id'],
                            value_id:          row['value_id']
                        }
                    ]);
                }
                if (response && response.body.data.length) {
                    let savedObject = response.data.data[0];
                    if (!row.id) {
                        let batchObject = this.uploadHistoryDialog.historyTable.data.filter(batch => batch.id === row.index_data_set_id)[0];
                        batchObject['qualitative_score'].unshift(savedObject);
                        this.uploadHistoryDialog.compare.enableAdd = false;
                    }
                    Object.assign(row, savedObject);
                }
            },
            deleteIndexScore:                   async function (row) {
                let confirm = await this.$confirm(`Are you sure you want to delete Raw Score of Batch #ID ` +
                    row['index_data_set_id'] + ` for country ` + this.countryData[row['country_id']].name + `?`, 'Warning', {
                    confirmButtonText: 'Yes', cancelButtonText: 'No', type: 'warning'
                }).then(() => true).catch(() => false);
                if (!confirm) return;
                let deleteResponse;
                if (this.sourceTable.selectedRow.type == 'quant')
                    deleteResponse = await API.riskIndex.indexScore.delete([row['id']]);
                else
                    deleteResponse = await API.riskIndex.qualitativeScore.delete([row['id']]);
                if (!deleteResponse)
                    return;
                this.$notify.success({
                    title:   `Delete successfully`,
                    message: `Raw Score of Batch #ID ` + row['index_data_set_id'] + ` for country ` +
                                 this.countryData[row['country_id']].name + ` is successfully deleted.`
                });
                let batchObject = this.uploadHistoryDialog.historyTable.data.filter(batch => batch.id === row.index_data_set_id)[0];

                if (this.sourceTable.selectedRow.type == 'quant')
                    batchObject['index_score'].splice(batchObject['index_score'].indexOf(row), 1);
                else
                    batchObject['qualitative_score'].splice(batchObject['qualitative_score'].indexOf(row), 1);
                Object.assign(row, {
                    id:               undefined,
                    normalized_score: undefined,
                    raw_score:        undefined,
                    value_id:         undefined
                });
            },
            addNewRow() {
                this.uploadHistoryDialog.compare.enableAdd = true;
            },
            countrySearch(queryString, cb) {
                let results = queryString ? this.countryList.filter(this.createFilter(queryString)) : this.countryList;
                cb(results);
            },
            createFilter(queryString) {
                return (country) => {
                    return (country.value.toLowerCase().indexOf(queryString.toLowerCase()) === 0);
                };
            },
            handleCountrySelect(item, row) {
                row.country_id = item.country_id;
            },
            disableCountry(row) {
                for (const [key, value] of Object.entries(row)) {
                    if (key != 'country_name' && key != 'country_id' && value.id != undefined)
                        return true;
                }
            },
            closeComparisionDialog() {
                this.uploadHistoryDialog.compare.filter_name = '';
                this.$refs.sourceHistoryTable.$refs.elTable.clearSelection();
            },
            filterQualitativeParentSource(query = false) {
                let filtered         = [];
                let selectedRiskArea = this.sourceTable.filters.riskArea.value;
                filtered             = this.setQualitativeParentSources();
                if (query) {
                    let re   = new RegExp(query.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), "ig");
                    filtered = filtered.filter(source => {
                        re.lastIndex = 0;
                        return re.test(source.name + source.abbreviation);
                    });
                }

                if (selectedRiskArea) {
                    filtered = filtered.filter(row => row['risk_areas'].includes(selectedRiskArea));
                }
                this.setQualitativeParentSources(filtered);
            },
            setQualitativeParentSources(data = false) {
                if (!data) {
                    data = Object.values(this.sourceData).filter(source => source.type === 'qual' && !source.parent_source_id);
                }
                this.sourceTable.qualitativeParentSources = data.sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));
                return this.sourceTable.qualitativeParentSources;
            }
        },
        watch:       {
            sourceData: {
                handler: function () {
                    this.constructSourceTableData();
                },
                deep:    true
            }
        },
        mounted() {
            this.constructSourceTableData();
            this.loadSourceWeightData();
        }
    };
</script>

<style scoped lang="scss">
    @import "@/styles/_base.scss";

    .source-library-table {
        &::v-deep {
            tbody tr td {
                padding: 5px 0;
            }
        }

        .capitalize {
            text-transform: capitalize;
        }

        .search-bar {
            margin-bottom: 10px;

            .el-select {
                display: block;
            }
        }

        .grey-name {
            color: $color-grey-dark;
            font-size: 12px;
        }

        .count-badge {
            background-color: $color-grey-dark;
            border-radius: 10px;
            color: #FFFFFF;
            display: inline-block;
            font-size: 12px;
            height: 18px;
            line-height: 18px;
            padding: 0 6px;
            text-align: center;
            white-space: nowrap;
            border: 1px solid #FFFFFF;
            margin-left: 5px;
            cursor: pointer;

            &:hover {
                background-color: $color-blue;
            }
        }
    }

    .full-screen {
        position: absolute;
        top: 20px;
        right: 45px;
        padding: 0;
        background: transparent;
        border: none;
        outline: none;
        cursor: pointer;
        font-size: 16px;
    }

    .source-history-table {
        margin-bottom: 20px;

        &::v-deep {
            .last-confirmed-btn {
                float: right;
                margin-left: 5px;
                padding: 4px;
            }


            .el-table {
                margin-top: 10px;

                .el-table__header-wrapper {
                    th.is-leaf {
                        background-color: #DCF7FB;
                        color: #5C5C5C;
                        padding: 7px 0;

                        .cell {
                            word-break: break-word;
                        }
                    }

                    .gutter {
                        background-color: #DCF7FB;
                    }
                }

                .el-loading-spinner {
                    top: 23px;
                }

                &::v-deep {
                    thead {
                        th {
                            border-bottom: 1px solid #33B4E9;
                        }
                    }
                }
            }

            .pagination-bar {
                margin: 10px 0;
            }
        }
    }

    .upload-history-dialog {
        .text-right {
            text-align: right;
        }


        &::v-deep {
            .el-table__header {
                border-bottom: 1px solid #33B4E9;
            }

            .el-dialog__header {
                border-bottom: 1px solid #EBEEF5;
            }

            .dialog-content {
                .top-btn {
                    float: right;

                    .actionBtn {
                        min-width: 150px;

                        .el-button {
                            min-width: 150px;
                            text-align: left;
                        }

                        i {
                            float: right;
                        }
                    }
                }

                .el-dialog__body {
                    padding: 10px 20px;
                }

                .el-date-editor {
                    right: 5px;
                }

                table {
                    .el-date-editor {
                        width: 100%;
                    }
                }

                .pagination-bar {
                    width: 350px;

                    .pagination-wrap {
                        text-align: left;
                    }
                }
            }

            .el-dialog__body {
                padding: 10px 20px;
            }

            .el-table {
                .active-cell {
                    background-color: #dcf7fb;;
                }

                .change-normalization-div {
                    height: 28px;

                    & > div {
                        width: calc(100% - 36px);
                        display: inline-block;
                    }

                    .el-button {
                        padding: 4px;
                        float: right;
                        top: 3px;
                        position: relative;
                    }
                }

                .cell {
                    word-break: break-word;
                }

                thead {
                    &.is-group {
                        th {
                            background-color: #DCF7FB;
                            color: #5C5C5C;
                            padding: 5px 0;
                        }
                    }
                }

                .btn-add {
                    color: #e6a23c;
                }

                .btn-delete {
                    color: #dd6161;
                    font-weight: bold;
                    font-size: 18px;
                    padding: 0;
                }

                .inline-input {
                    width: 100%;

                    .el-input.is-disabled {
                        .el-input__inner {
                            border: none;
                            color: #606266;
                            padding: 0;
                            background-color: inherit;
                            font-size: 14px;
                            cursor: inherit;
                        }
                    }
                }
            }
        }

        .box-card {
            &::v-deep {

                .el-card__header {
                    padding: 10px 20px;
                    text-align: right;
                    border-bottom: none;

                    .el-button--text {
                        padding: 6px 0;
                    }
                }

                .el-card__body {
                    padding: 0 20px 10px;
                }


                .el-input-group__append {
                    &:hover {
                        background: #26b4e9;
                        color: #FFFFFF;
                        border-color: #26b4e9;
                    }
                }

                .el-table__header-wrapper {
                    tr:first-child {
                        th:first-child {
                            background: #DCF7FB;
                        }

                        th:first-child {
                            .cell {
                                height: 28px;

                                .el-input {
                                    padding: 0;
                                    max-width: 250px;
                                    line-height: 28px;
                                }
                            }
                        }
                    }

                    th.is-leaf {
                        background-color: #DCF7FB;
                        color: #5C5C5C;
                        padding: 2.5px 0;
                        font-weight: normal;
                    }

                    .gutter {
                        width: 0;
                        display: none;
                    }
                }

                .el-table__body-wrapper {
                    max-height: calc(70vh - 260px) !important;
                    overflow-y: auto;

                    .el-table__body {
                        background-color: #00FFFF;
                    }
                }
            }
        }

        .box-card.quantitative-table {
            &::v-deep {
                .el-table__header-wrapper {
                    tr:first-child {
                        th:nth-child(2n+2) {
                            background: #DBE6E0;
                        }

                        th:nth-child(2n+3) {
                            background: #f9dbdb;
                        }

                        th:nth-child(2n) {
                            background: #DBE6E0 !important;
                        }

                    }

                    th:nth-child(6n+1), th:nth-child(6n+2), th:nth-child(6n+3) {
                        background: #DBE6E0;
                    }

                    th:nth-child(6n+4), th:nth-child(6n+5), th:nth-child(6n+6) {
                        background: #f9dbdb;
                    }
                }

                .el-table__body-wrapper {
                    .el-table__body {

                        td:nth-child(6n+2), td:nth-child(6n+3), td:nth-child(6n+4) {
                            background: #DBE6E0;
                        }

                        td:nth-child(6n+5), td:nth-child(6n+6), td:nth-child(6n+7) {
                            background: #f9dbdb;
                        }
                    }
                }
            }
        }

        .box-card.qualitative-table {
            &::v-deep {
                .el-table__header-wrapper {
                    tr:first-child {
                        th:nth-child(2n+2) {
                            background: #DBE6E0;
                        }

                        th:nth-child(2n+3) {
                            background: #f9dbdb;
                        }

                        th:nth-child(2n) {
                            background: #DBE6E0 !important;
                        }

                    }

                    th:nth-child(4n+1), th:nth-child(4n+2) {
                        background: #DBE6E0;
                    }

                    th:nth-child(4n+3), th:nth-child(4n+4) {
                        background: #f9dbdb;
                    }
                }

                .el-table__body-wrapper {
                    .el-table__body {

                        td:nth-child(4n+2), td:nth-child(4n+3) {
                            background: #DBE6E0;
                        }

                        td:nth-child(4n+4), td:nth-child(4n+5) {
                            background: #f9dbdb;
                        }
                    }
                }
            }
        }
    }

    .risk-area-option, .parent-source-filter {
        span:first-child {
            float: left
        }

        span:last-child {
            color: $color-grey-dark;
            float: right;
            font-size: 13px;
            margin-left: 10px;

            &:hover {
                color: $color-blue;
            }
        }
    }

    .risk-area-popover-content {
        border-top: 1px solid $color-grey;
        padding-top: 5px;
        margin-top: -5px;

        .included, .excluded {
            h3 {
                color: $color-grey-dark;
                font-weight: normal;
                font-size: 12px;
                margin: 0;
            }

            p {
                margin: 5px 0 5px 10px;
            }
        }

        .included {
            margin-bottom: 10px;
        }
    }

    .el-dropdown-menu.el-popper {
        min-width: 150px;

        .disabled {
            color: #bbb;
        }
    }
</style>
